/* eslint-disable jsx-a11y/media-has-caption */
import * as React from 'react';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Avatar, AvatarGroup, Box, IconButton, Stack, Typography,
} from '@mui/material';
import {
  ClosableModal, ErrorMessage, PrimaryLoadingButton,
  PrimaryTextField,
  ProgressBar,
  SecondaryButton, VideoPreviewPlayer,
} from 'components';
import { SecondaryBadge } from 'components/molecules/custom-badges';
import useScreenRecorder from 'base/core/hooks/use-screen-recorder';
import JsFileDownloader from 'js-file-downloader';

import {
  CloseSquareFillIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
  MaximizeSquareFillIcon,
  MaximizeWebCamIcon,
  MiniimizeSquareFillIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  StopCircleFillIcon,
  StopCircleIcon,
} from 'assets/icons';
import { FileExtensions, UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { dateFormat } from 'utils/formats';
import { useSelector, useDispatch } from 'react-redux';
import { selectSingleConversation, selectUploading } from 'pages/sessions/components/message-hub/selectors';
import { CourseTypes, SessionType } from 'pages/sessions/interface';
import { chatActions } from 'pages/sessions/components/message-hub/slice';
import useFileUpload from 'base/core/hooks/use-file-upload';
import {
  getAvatarLetters, getUserName, replaceExt, retry, sleep,
} from 'utils/common-utils';
import { mediaLibraryAction } from 'pages/media-library/slice';
import { selectEnterprise } from 'base/core/selectors';
import { useConfirmation } from 'base/common';
import { avatarStyle } from '../../styles';
import './style.scss';

interface Props {
  fullscreen: boolean;
  showSideBar: boolean;
  enterFullScreen: () => void;
  exitFullScreen: () => void;
  toggleSideBar: (isDisplay: boolean) => void;
  container: Element | (() => Element | null) | null | undefined;
}

const TopBar = ({
  fullscreen, showSideBar, enterFullScreen, exitFullScreen, toggleSideBar, container,
}: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { conversationId } = useParams();
  const webCamVideoRef = React.useRef<HTMLVideoElement>(null);
  const [webCamFullScreen, setWebCamFullScreen] = React.useState(false);
  const [attachSessionError, setAttachSessionError] = React.useState('');
  const [recordFileName, setRecordFileName] = React.useState('');
  const [isRemoteFileDownloading, setIsRemoteFileDownloading] = React.useState(false);

  const { pathname } = useLocation();
  const isRecordOnlyView = React.useMemo(() => pathname === '/media/record-content', [pathname]);

  const isRecordFileNameValid = React.useMemo(() => {
    if (!isRecordOnlyView && recordFileName.length === 0) return { isValid: false, error: '' };
    if (isRecordOnlyView && recordFileName.length < 3) return { isValid: false, error: 'File name should contain atleast 3 characters' };
    if (recordFileName.length > 100) return { isValid: false, error: 'File name should be less than 100 characters' };

    return { isValid: true, error: '' };
  }, [recordFileName, isRecordOnlyView]);

  const {
    users, userSession, session,
  } = useSelector(selectSingleConversation);
  const enterprise = useSelector(selectEnterprise);
  const uploading = useSelector(selectUploading);
  const { progress, handleContentDataUpload } = useFileUpload();

  const confirm = useConfirmation();

  const remoteContentUrl = React.useRef('');
  const remoteFileName = React.useRef('');

  // handle recording
  const onStartRecording = () => {
    setWebCamFullScreen(false);
  };

  const onStopRecording = () => {};

  const onStartConfirmation = (resolve: any, reject: any) => {
    confirm({
      variant: 'confirm',
      message: 'Would you like to record this in fullscreen mode?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'No',
      containerRef: container,
      catchOnCancel: true,
    }).then(() => {
      toggleSideBar(false);
      enterFullScreen();
      resolve();
    }).catch(() => {
      resolve();
    });
  };

  const {
    mediaBlob,
    mediaBlobUrl,
    pauseRecording,
    resumeRecording,
    startRecording,
    stopRecording,
    status,
    counter,
    beginCounter,
    previewWebcamStream,
    clearBlobUrl,
  } = useScreenRecorder({
    onStart: onStartRecording, onStop: onStopRecording, onStartConfirmation,
  });

  React.useEffect(() => {
    if (status === 'recording') {
      if (webCamVideoRef.current) {
        webCamVideoRef.current.srcObject = previewWebcamStream;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webCamFullScreen, status]);

  const webCamPreviwer = !webCamFullScreen ? (
    <div className="webcam-wrapper">
      <div className="webcam-overlay">
        <IconButton onClick={() => { setWebCamFullScreen(true); }}>
          <MaximizeWebCamIcon sx={{ color: 'white' }} />
        </IconButton>
      </div>
      <video ref={webCamVideoRef} autoPlay muted />
    </div>
  ) : (
    <div className="webcam-full-screen-wrapper">
      <div className="webcam-full-video-wrapper">
        <div className="btn-wrapper">
          <IconButton onClick={() => { setWebCamFullScreen(false); }}>
            <MaximizeSquareFillIcon sx={{ color: 'white' }} />
          </IconButton>
        </div>
        <video ref={webCamVideoRef} autoPlay muted />
      </div>
    </div>
  );

  // handle exit from digital coaching tool
  const confirmExitHandler = () => {
    navigate(`/sessions/messages/${conversationId}`);
  };

  const backHandler = () => {
    navigate(-1);
  };

  const handleExit = () => {
    confirm({
      variant: 'confirm',
      icon: 'warning',
      message: 'Are you sure you want to exit?',
      confirmButtonTxt: 'Yes',
      cancelButtonTxt: 'No',
      containerRef: container,
    })
      .then(() => {
        confirmExitHandler();
      });
  };

  const processRecordingFile = async (): Promise<UploadResponse | undefined> => {
    dispatch(chatActions.setStartUploading());
    const blob: any = mediaBlob;
    blob.lastModified = new Date();
    const recordFile = new File([blob], `${uuidv4()}.webm`, {
      type: blob.type,
    });
    const res = await handleContentDataUpload(
      null,
      '',
      UploadScenarios.sessionFeedback,
      recordFile,
    );
    if (res.statusCode === 201) {
      await sleep(5000);
      dispatch(chatActions.setEndUploading());
      return {
        ...res,
        url: res.url.replace('.webm', '.mp4'),
        extension: FileExtensions.mp4.toUpperCase(),
      };
    }
    dispatch(chatActions.setEndUploading());
    return {
      name: '',
      url: '',
      extension: '',
      category: '',
      statusCode: 400,
      message: 'Upload failed',
    };
  };

  const handleAttachToSession = async () => {
    const processResponse: UploadResponse | undefined = await processRecordingFile();
    if (
      processResponse
      && (processResponse.statusCode === 200 || processResponse.statusCode === 201)) {
      await new Promise((resolve, reject) => {
        dispatch(chatActions.sendMessage({
          body: '',
          attachments: [{
            fileUrl: processResponse.url,
            fileName: processResponse.name,
            fileType: processResponse.extension.toUpperCase(),
            fileCategory: processResponse.category || 'video',
            thumbnail: replaceExt(processResponse.url, '.0000001.jpg'),
          }],
          resolve,
          reject,
        }));
      });

      confirm({
        variant: 'info',
        icon: 'success',
        message: 'Your recorded video has been attached to the session. Click OK button to return to the chat',
        confirmButtonTxt: 'Ok',
        containerRef: container,
      }).then(() => {
        confirmExitHandler();
      });
    } else {
      setAttachSessionError('Error while attaching the recording to the session');
    }
  };

  const handleDownloadRecording = async () => {
    const link = document.createElement('a');
    if (!link) return;

    const url = isRecordOnlyView ? remoteContentUrl.current : (mediaBlobUrl as string);
    let filename = '';

    if (isRecordOnlyView) {
      filename = remoteFileName.current;
      setIsRemoteFileDownloading(true);
    } else if (isRecordFileNameValid.isValid) {
      filename = `${recordFileName}.webm`;
    } else {
      filename = `Review content - ${moment().format('MMM_Do_YYYY_hh_mm_A')}.webm`;
    }

    try {
      await retry(() => new JsFileDownloader({ url, filename }), 10, 10000);
    } catch (error) {
      throw new Error('Failed to download the file after 5 retries');
    }
  };

  const handleSaveToMedia = async (isDownload: boolean) => {
    const processResponse: UploadResponse | undefined = await processRecordingFile();
    if (processResponse && processResponse.statusCode === 201) {
      try {
        dispatch(chatActions.setStartUploading());
        await new Promise((resolve, reject) => {
          const fileName = isRecordFileNameValid.isValid
            ? `${recordFileName}`
            : `Review content - ${moment().format('MMM_Do_YYYY_hh_mm_A')}`;

          remoteContentUrl.current = processResponse.url;
          remoteFileName.current = `${fileName}.mp4`;

          dispatch(mediaLibraryAction.uploadMedia({
            path: '/root',
            expertId: null,
            entId: enterprise.id,
            contentUrl: processResponse.url,
            fileName,
            fileType: processResponse.extension.toUpperCase(),
            fileCategory: processResponse.category ?? null,
            title: fileName,
            description: '',
            isExternalFile: false,
            thumbnailGenerated: '',
            resolve,
            reject,
          }));
        });
        dispatch(chatActions.setEndUploading());
        if (isDownload) {
          await handleDownloadRecording();
          navigate('/media-library/root');
        } else {
          confirm({
            variant: 'info',
            icon: 'success',
            message: 'Your recorded video has been saved to the media',
            confirmButtonTxt: 'Ok',
            blockClosing: true,
            containerRef: container,
          })
            .then(() => {
              navigate('/media-library/root');
            })
            .catch(() => {});
        }
      } catch (error) {
        console.error(error);
        dispatch(chatActions.setEndUploading());
        setAttachSessionError('Error while processing the recorded video');
      }
    } else {
      dispatch(chatActions.setEndUploading());
      setAttachSessionError('Error while saving the recording to the media');
    }
  };

  React.useEffect(() => {
    if (conversationId && (session.sessionType !== SessionType.digitalSession
      && session.courseType !== CourseTypes.interactive)) {
      navigate(`/sessions/messages/${conversationId}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, navigate]);

  const handleRecordingName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRecordFileName(event.target.value);
  };

  return (
    <Box
      className="row"
      sx={{
        pl: 2, py: 1.5, border: 1, borderColor: 'divider', backgroundColor: 'background.paper',
      }}
    >
      <ClosableModal
        open={status === 'stopped'}
        onClose={clearBlobUrl}
        title="Your session recording"
        subTitle=""
        modalClass=""
        container={container}
      >
        <>
          <div className="row">
            <div className="col-12 mb-4">
              <VideoPreviewPlayer url={mediaBlobUrl as string} />
            </div>
          </div>
          <div className="row">
            <div className="col-12 mb-3">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {`If you're happy with your recording, attach it to your ${conversationId ? 'session' : 'media'}.`}
                {!isRecordOnlyView && (` Or, if you want to download your recording and attach it later,
                  use the download button below.`)}
              </Typography>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <PrimaryTextField
                placeholder={`Recording file name ${isRecordOnlyView ? '(required)' : '(optional)'}`}
                onChange={handleRecordingName}
              />
              {!isRecordFileNameValid.isValid && (
                <div className="mt-1 text-center">
                  <ErrorMessage>
                    {isRecordFileNameValid.error}
                  </ErrorMessage>
                </div>
              )}
            </div>
          </div>
          <div className="row mt-3">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <PrimaryLoadingButton
                fullWidth
                onClick={() => {
                  if (isRecordOnlyView) {
                    handleSaveToMedia(false);
                  } else {
                    handleAttachToSession();
                  }
                }}
                loading={uploading || isRemoteFileDownloading}
                disabled={
                  isRecordOnlyView && (!isRecordFileNameValid.isValid || isRemoteFileDownloading)
                }
              >
                {isRecordOnlyView ? 'Save to my media' : 'Attach to the session'}
              </PrimaryLoadingButton>
            </div>
            <div className="col-12 col-md-6">
              <PrimaryLoadingButton
                fullWidth
                onClick={() => {
                  if (isRecordOnlyView) {
                    handleSaveToMedia(true);
                  } else {
                    handleDownloadRecording();
                  }
                }}
                loading={uploading || isRemoteFileDownloading}
                disabled={
                  isRecordOnlyView && (!isRecordFileNameValid.isValid || isRemoteFileDownloading)
                }
              >
                {isRecordOnlyView ? 'Save to media and download' : 'Download the recording'}
              </PrimaryLoadingButton>
            </div>
            {attachSessionError !== '' && !uploading && (
              <div className="mt-2 text-center">
                <ErrorMessage>
                  {attachSessionError}
                </ErrorMessage>
              </div>
            )}
            {attachSessionError === '' && uploading && (
              progress > -1 && (
                <Box sx={{ mt: 2 }}>
                  <ProgressBar progress={progress} />
                </Box>
              )
            )}
          </div>
          <div className="row mt-3">
            <div className="col-12 mb-2 mb-md-0">
              <SecondaryButton fullWidth onClick={clearBlobUrl}>Discard recording</SecondaryButton>
            </div>
          </div>
        </>
      </ClosableModal>
      <div
        className="col-md-12 col-lg-12 col-xl-11"
      >
        <div className="row align-items-center justify-content-between">
          <div className="px-0 col-md-auto d-flex align-items-center">
            <Box
              sx={{
                py: 1.5,
                px: 1,
                border: 1,
                borderTopRightRadius: 6,
                borderBottomRightRadius: 6,
                borderColor: 'divider',
                lineHeight: 1,
                cursor: 'pointer',
                backgroundColor: 'background.paper',
                marginRight: '15px',
                marginLeft: '-18px',
                marginBottom: '-100vh',
                zIndex: 999,
              }}
              onClick={() => toggleSideBar(!showSideBar)}
            >
              {showSideBar ? (
                <DoubleArrowLeftIcon sx={{ color: 'primary.dark', fontSize: 12 }} />
              ) : (
                <DoubleArrowRightIcon sx={{ color: 'primary.dark', fontSize: 12 }} />
              )}
            </Box>
            {conversationId && users && users.length > 0 && (
              <>
                <AvatarGroup max={2} sx={{ mr: 1 }}>
                  <Avatar
                    key={users[0]?.id}
                    sx={[avatarStyle]}
                    src={users[0]?.profileImgUrl}
                    alt={getAvatarLetters(users[0])}
                  >
                    {getAvatarLetters(users[0])}
                  </Avatar>
                </AvatarGroup>
                <div>
                  <Typography variant="h6" sx={{ color: 'text.primary', lineHeight: 1 }}>
                    {`Session with ${getUserName(users[0])}`}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1, mt: 0.5 }}>
                    {userSession.startAt ? `Session started on ${dateFormat(userSession.startAt)}` : ''}
                  </Typography>
                </div>
                <SecondaryBadge sx={{ ml: 3 }}>
                  {session && session.sessionType ? session.sessionType : ''}
                </SecondaryBadge>
              </>
            )}
          </div>

          <div className="col-md-auto d-flex justify-content-end">

            <Stack direction="row" spacing={1.5}>

              {(conversationId || isRecordOnlyView) && (
                <div className="d-flex align-items-center">
                  {status === 'recording' || status === 'paused' ? (
                    <Stack alignItems="center" direction="row" spacing={1} sx={{ color: '#FF5858' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <StopCircleFillIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" sx={{ color: 'inherit', minWidth: '125px' }}>
                          {`Recording ${new Date(counter * 1000).toISOString().substring(11, 11 + 8)}`}
                        </Typography>
                      </Box>
                      {status === 'paused' ? (
                        <PlayCircleIcon className="cursor-pointer" onClick={resumeRecording} />
                      ) : (
                        <PauseCircleIcon className="cursor-pointer" onClick={pauseRecording} />
                      )}
                      <StopCircleIcon className="cursor-pointer" onClick={stopRecording} />
                    </Stack>
                  ) : (
                    <div className="record-feedback">
                      <IconButton onClick={startRecording} sx={{ color: 'primary.dark', p: 0 }} disableRipple>
                        <StopCircleFillIcon sx={{ mr: 1 }} />
                        <Typography variant="body2" sx={{ color: 'inherit' }}>
                          Record feedback
                        </Typography>
                      </IconButton>
                    </div>
                  )}
                </div>
              )}
              {fullscreen ? (
                <IconButton onClick={exitFullScreen} sx={{ p: 0 }}>
                  <MiniimizeSquareFillIcon sx={{ color: 'primary.dark' }} />
                </IconButton>
              ) : (
                <IconButton onClick={enterFullScreen} sx={{ p: 0 }}>
                  <MaximizeSquareFillIcon sx={{ color: 'primary.dark' }} />
                </IconButton>
              )}
              {/* <UploadSquareFillIcon sx={{ color: 'primary.dark' }} /> */}
              {conversationId && (
                <IconButton onClick={() => { handleExit(); }} sx={{ p: 0 }}>
                  <CloseSquareFillIcon sx={{ color: 'primary.dark' }} />
                </IconButton>
              )}
              {!conversationId && (
                <IconButton onClick={() => { backHandler(); }} sx={{ p: 0 }}>
                  <CloseSquareFillIcon sx={{ color: 'primary.dark' }} />
                </IconButton>
              )}
            </Stack>

          </div>
        </div>
      </div>
      {status === 'begin' ? (
        <div className="recorderCountDown position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
          <Typography variant="h3" color="white">
            {`Recording will start in ${String(beginCounter).padStart(2, '0')}`}
          </Typography>
        </div>
      ) : null}
      {status === 'recording' ? (
        webCamPreviwer
      ) : null}
    </Box>
  );
};

export default TopBar;
